import { useCallback, useEffect, useState } from 'react';
import { enUS as en, ru } from 'date-fns/locale';
import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import {
  DateRangePicker,
  DateRangePickerSlotProps,
  LocalizationProvider,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { withControl } from '@features/effector-form/hoc/withControl';
import { getSafePickerLocale } from '@shared/utils/getSafePickerLocale';

type Props = {
  sx?: object;
  sxForm?: object;
  disableFuture?: boolean;
  slotProps?: DateRangePickerSlotProps<true>;
};

const EN = 'en';
const LNG = localStorage.getItem('lang') || EN;
const PICKER_POPPER_SELECTOR = '.MuiPickerPopper-root, .MuiPickersPopper-root';

const isEventInsidePickerPopper = (event: Event) => {
  const target = event.target as HTMLElement | null;

  if (target?.closest(PICKER_POPPER_SELECTOR)) {
    return true;
  }

  if (!(event instanceof MouseEvent)) {
    return false;
  }

  return Array.from(document.querySelectorAll(PICKER_POPPER_SELECTOR)).some((element) => {
    const rect = element.getBoundingClientRect();

    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  });
};

const useCloseOnScroll = (isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const closeOnScroll = (event: Event) => {
      if (isEventInsidePickerPopper(event)) {
        return;
      }

      onClose();
    };

    document.addEventListener('wheel', closeOnScroll, true);
    document.addEventListener('touchmove', closeOnScroll, true);
    document.addEventListener('scroll', closeOnScroll, true);

    return () => {
      document.removeEventListener('wheel', closeOnScroll, true);
      document.removeEventListener('touchmove', closeOnScroll, true);
      document.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [isOpen, onClose]);
};

export const DateRangePickerControl = withControl<Props>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    sx = {},
    sxForm = {},
    isError,
    disableFuture,
    slotProps,
  }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const openCalendar = () => setIsCalendarOpen(true);
    const closeCalendar = useCallback(() => setIsCalendarOpen(false), []);

    useCloseOnScroll(isCalendarOpen, closeCalendar);

    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={LNG === EN ? en : ru}
        localeText={getSafePickerLocale(LNG)}
      >
        <FormControl required={required} disabled={disabled} sx={sxForm}>
          {label && <FormLabel error={isError}>{label}</FormLabel>}

          <DateRangePicker
            slots={{
              field: SingleInputDateRangeField,
              openPickerIcon: CalendarToday,
            }}

            value={value}
            onChange={(newValue) => setValue(newValue)}
            open={isCalendarOpen}
            onOpen={openCalendar}
            onClose={closeCalendar}
            readOnly={readOnly}
            disabled={disabled}
            disableFuture={disableFuture}
            reduceAnimations
            slotProps={{
              ...slotProps,
              openPickerButton: {
                size: 'small',
              },
              desktopTransition: {
                timeout: 0,
              },
            }}
            sx={{
              '& input, & input::placeholder, & .MuiPickersInputBase-sectionContent': {
                textTransform: 'lowercase',
              },
              ...sx,
            }}
          />

          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      </LocalizationProvider>
    );
  }
);
